import { useTranslation } from 'react-i18next';
import { DEFAULT_DATE_LABELS, type DateFieldLabels } from '@attendance/ui';

/* ===========================================================================
 * THE APP'S WORDS FOR THE DATE CONTROLS.
 *
 * `packages/ui` has no i18n and must not grow one — three apps consume it and
 * they do not share a bundle. So `DateField`, `DateRangeField` and `TimeField`
 * take an English default object and accept an override, exactly the way
 * `Pagination` does. This hook is apps/web's override, built once so the
 * thirty-seven call sites that are still on `<input type="date">` have one
 * place to point at as they convert.
 *
 * `useMemo` is deliberately absent: the object is rebuilt on every render and
 * that is fine — it is thirty-odd string lookups, and memoising it on `t`
 * (which is stable across a language change) is how a picker ends up still
 * speaking English after the reader switches to Arabic.
 * ======================================================================== */
export function useDateLabels(): DateFieldLabels {
  const { t } = useTranslation();
  return {
    ...DEFAULT_DATE_LABELS,
    monthNames: [
      t('dateField.month.january', 'January'),
      t('dateField.month.february', 'February'),
      t('dateField.month.march', 'March'),
      t('dateField.month.april', 'April'),
      t('dateField.month.may', 'May'),
      t('dateField.month.june', 'June'),
      t('dateField.month.july', 'July'),
      t('dateField.month.august', 'August'),
      t('dateField.month.september', 'September'),
      t('dateField.month.october', 'October'),
      t('dateField.month.november', 'November'),
      t('dateField.month.december', 'December'),
    ],
    /* MONDAY FIRST, and two letters rather than the three `myAttendance.weekday.*`
       already carries: a 36px day cell is 36px wide, and "Mon" at the label step
       sets the column wider than the cells under it. Different length, different
       job, so a separate key rather than a reuse that would drift. */
    weekdayShort: [
      t('dateField.weekdayShort.mon', 'Mo'),
      t('dateField.weekdayShort.tue', 'Tu'),
      t('dateField.weekdayShort.wed', 'We'),
      t('dateField.weekdayShort.thu', 'Th'),
      t('dateField.weekdayShort.fri', 'Fr'),
      t('dateField.weekdayShort.sat', 'Sa'),
      t('dateField.weekdayShort.sun', 'Su'),
    ],
    weekdayLong: [
      t('dateField.weekdayLong.mon', 'Monday'),
      t('dateField.weekdayLong.tue', 'Tuesday'),
      t('dateField.weekdayLong.wed', 'Wednesday'),
      t('dateField.weekdayLong.thu', 'Thursday'),
      t('dateField.weekdayLong.fri', 'Friday'),
      t('dateField.weekdayLong.sat', 'Saturday'),
      t('dateField.weekdayLong.sun', 'Sunday'),
    ],
    placeholder: t('dateField.placeholder', 'DD-MM-YYYY'),
    triggerAria: t('dateField.aria', 'Date'),
    previousMonth: t('dateField.previousMonth', 'Previous month'),
    nextMonth: t('dateField.nextMonth', 'Next month'),
    today: t('dateField.today', 'Today'),
    clear: t('dateField.clear', 'Clear'),
    apply: t('dateField.apply', 'Apply'),
    /* Reused verbatim, per design-rules §5: these six labels already exist and
       already mean exactly this. Inventing a second key for a string the app
       has would give one phrase two translations to drift apart. */
    reset: t('common.reset', 'Reset'),
    from: t('reports.scope.from', 'From'),
    to: t('reports.scope.to', 'To'),
    presets: {
      last7: t('reports.scope.preset.last7', 'Last 7 days'),
      last30: t('reports.scope.preset.last30', 'Last 30 days'),
      thisMonth: t('reports.scope.preset.thisMonth', 'This month'),
      lastMonth: t('reports.scope.preset.lastMonth', 'Last month'),
      thisQuarter: t('dateField.preset.thisQuarter', 'This quarter'),
      custom: t('reports.workspace.preset.custom', 'Custom…'),
    },
    monthLength: (month, days) =>
      t('dateField.monthLength', '{{month}} has {{days}} days.', { month, days }),
    unreadable: t('dateField.unreadable', 'Write the date as DD-MM-YYYY.'),
    monthRange: t('dateField.monthRange', 'A month is between 01 and 12.'),
    outOfBounds: (min, max) =>
      t('dateField.outOfBounds', 'Pick a day between {{min}} and {{max}}.', { min, max }),
    time: {
      placeholder: t('dateField.time.placeholder', 'HH:MM'),
      triggerAria: t('dateField.time.aria', 'Time'),
      listAria: t('dateField.time.listAria', 'Times'),
      unreadable: t(
        'dateField.time.unreadable',
        'Write the time as HH:MM, on a 24-hour clock.'
      ),
    },
  };
}
